'use client'

import { useState } from 'react'
import { Play } from 'lucide-react'
import { useLocale } from '@/components/i18n/LocaleProvider'

type DocsYouTubeEmbedProps = {
  /** YouTube video id (the v= query value). */
  videoId: string
  title: string
  /** Optional caption / attribution under the player. */
  caption?: string
}

/**
 * Click-to-play YouTube facade: official thumbnail preview until Play
 * (faster docs load + progressive disclosure).
 */
export function DocsYouTubeEmbed({ videoId, title, caption }: DocsYouTubeEmbedProps) {
  const { t } = useLocale()
  const [playing, setPlaying] = useState(false)
  const id = encodeURIComponent(videoId)
  const watchUrl = `https://www.youtube.com/watch?v=${id}`
  const posterUrl = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
  const embedSrc = `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`

  return (
    <figure className="docs-youtube">
      <div className="docs-youtube-frame">
        {playing ? (
          <iframe
            src={embedSrc}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="docs-youtube-preview"
            onClick={() => setPlaying(true)}
            aria-label={`${t('common.play', 'Play')}: ${title}`}
          >
            {/* Remote YouTube CDN poster — not in next/image remotePatterns. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={posterUrl}
              alt=""
              className="docs-youtube-poster"
              loading="lazy"
              decoding="async"
            />
            <span className="docs-youtube-play" aria-hidden>
              <Play className="h-8 w-8 fill-current" strokeWidth={0} />
            </span>
            <span className="docs-youtube-preview-title">{title}</span>
          </button>
        )}
      </div>
      <figcaption className="docs-youtube-caption mt-2 text-body-sm text-muted-foreground">
        {caption ? <>{caption} </> : null}
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-dashed underline-offset-2 hover:decoration-solid"
        >
          {t('common.openOnYouTube', 'Open on YouTube')}
        </a>
      </figcaption>
    </figure>
  )
}
